import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import SettingIcon from "../../assets/images/settings-svgrepo-com.svg";
import {
  CloseOutlined,
  PoweroffOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useSocket } from "../../utils/Socket.Provider";
import { useGetConsultantProfileQuery } from "../../redux/rtk/apiRtk";
import { create } from "zustand";
import { IoIosNotifications } from "react-icons/io";
import { Button, Drawer, Badge, Radio } from "antd";

const useStore = create((set) => ({
  count: 0,
  inc: () => set((state) => ({ count: state.count + 1 })),
  reset: () => set({ count: 0 }),
}));

const logsetting = [
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    key={0}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M11.4892 3.17094C11.1102 1.60969 8.8898 1.60969 8.51078 3.17094C8.26594 4.17949 7.11045 4.65811 6.22416 4.11809C4.85218 3.28212 3.28212 4.85218 4.11809 6.22416C4.65811 7.11045 4.17949 8.26593 3.17094 8.51078C1.60969 8.8898 1.60969 11.1102 3.17094 11.4892C4.17949 11.7341 4.65811 12.8896 4.11809 13.7758C3.28212 15.1478 4.85218 16.7179 6.22417 15.8819C7.11045 15.3419 8.26594 15.8205 8.51078 16.8291C8.8898 18.3903 11.1102 18.3903 11.4892 16.8291C11.7341 15.8205 12.8896 15.3419 13.7758 15.8819C15.1478 16.7179 16.7179 15.1478 15.8819 13.7758C15.3419 12.8896 15.8205 11.7341 16.8291 11.4892C18.3903 11.1102 18.3903 8.8898 16.8291 8.51078C15.8205 8.26593 15.3419 7.11045 15.8819 6.22416C16.7179 4.85218 15.1478 3.28212 13.7758 4.11809C12.8896 4.65811 11.7341 4.17949 11.4892 3.17094ZM10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z"
      fill="#fdfdfd"
      color="#fdfdfd"
    />
  </svg>,
];

const toggler = [
  <svg
    width="20"
    height="20"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 448 512"
    key={0}
  >
    <path d="M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z" />
  </svg>,
];

function ConsultantHeader({ placement, name, subName, onPress }) {
  const { socket } = useSocket();
  const [visible, setVisible] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const isOnChatPage = window.location.hash.includes("#/chats");
  const { count, inc, reset } = useStore();
  const navigate = useNavigate();
  const { data, isLoading } = useGetConsultantProfileQuery("");

  useEffect(() => window.scrollTo(0, 0));

  const showDrawer = () => setVisible(true);
  const hideDrawer = () => setVisible(false);

  useEffect(() => {
    if (!isOnChatPage) {
      socket.on("consultant-message", (data) => {
        inc();
      });
    }
    if (isOnChatPage) {
      reset();
    }
    return () => {
      socket.off("consultant-message");
    };
  }, [isOnChatPage, socket]);

  useEffect(() => {
    if (data && !statusChanging) {
      setIsOnline(data?.isOnline);
    }
  }, [isLoading, data, statusChanging]);

  const handleStatusChange = (e) => {
    const newStatus = e === "online";

    if (newStatus !== isOnline) {
      setStatusChanging(true);
      setIsOnline(newStatus);
      socket.emit("setStatus", newStatus);
      console.log("Emitted status change:", newStatus);
    }
  };

  return (
    <>
      <div className="flex items-center h-16 m-0 px-2 sm:px-6 rounded-2xl  ">
        <div className="flex-1 md:flex-none md:w-1/4 flex items-center">
          <Button
            type="link"
            className="sidebar-toggler lg:hidden mr-4 ml-4  text-gray-700 flex items-center"
            onClick={onPress}
          >
            {toggler}
          </Button>
          <span className="text-pink-700 hidden sm:block font-extrabold text-4xl  capitalize">
            {subName}
          </span>
        </div>

        <div className="flex-1 md:flex-[3] flex items-center justify-end">
          <div className="flex items-center gap-1 sm:gap-3">
            {!isOnChatPage && count > 0 && (
              <div className="relative flex items-center">
                <button
                  onClick={() => {
                    navigate("/chats");
                    reset();
                  }}
                  className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  <IoIosNotifications
                    size={20}
                    className="text-yellow-700 sm:w-6 sm:h-6"
                  />
                  {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow-md">
                      {count > 99 ? "99+" : count}
                    </span>
                  )}
                </button>
              </div>
            )}

            <div className="flex items-center justify-center p-2 sm:p-4">
              <div className="flex bg-gray-200 rounded-full overflow-hidden shadow-md">
                {/* Online Button */}
                <button
                  onClick={() => handleStatusChange("online")}
                  className={`flex items-center px-2 text-white py-1.5 sm:px-4 sm:py-2 transition-all duration-300 ${
                    isOnline
                      ? "bg-green-500 "
                      : "bg-gray-200  hover:bg-gray-300"
                  } text-xs sm:text-sm`}
                >
                  <span
                    className={`w-3 h-3 rounded-full mr-2 transition-colors duration-300 ${
                      isOnline ? "bg-white" : "bg-green-500"
                    }`}
                  ></span>
                  <p
                    className={
                      isOnline ? "text-white font-bold" : "text-gray-700"
                    }
                  >
                    Online
                  </p>
                </button>

                {/* Offline Button */}
                <button
                  onClick={() => handleStatusChange("offline")}
                  className={`flex items-center px-2 py-1.5 sm:px-4 sm:py-2 transition-all duration-300 ${
                    !isOnline
                      ? "bg-red-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  } text-xs sm:text-sm`}
                >
                  <span
                    className={`w-3 h-3 rounded-full mr-2 transition-colors duration-300 ${
                      !isOnline ? "bg-white" : "bg-red-500"
                    }`}
                  ></span>
                  <p
                    className={
                      !isOnline ? "text-white font-bold" : "text-gray-700 "
                    }
                  >
                    Offline
                  </p>
                </button>
              </div>
            </div>

            <Button
              type="link"
              onClick={showDrawer}
              className="flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SettingOutlined className="text-xl text-[#A40165] sm:text-3xl" />
            </Button>
          </div>
        </div>
      </div>

      <Drawer
        className="settings-drawer"
        mask={true}
        width={360}
        onClose={hideDrawer}
        placement={placement}
        visible={visible}
        closeIcon={<CloseOutlined className="pl-2" />}
        title="Settings"
      >
        <div className="flex flex-col">
          <div className="py-6">
            <div className="mb-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
              <h4 className="mb-3 font-medium text-gray-800">
                Availability Status
              </h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Badge status={isOnline ? "success" : "error"} />
                  <span className="ml-2 text-gray-700">
                    {isOnline ? "Online" : "Offline"}
                  </span>
                </div>
              </div>
              <p className="text-xs mt-2 text-gray-600">
                {isOnline
                  ? "You are visible to clients and can receive consultation requests."
                  : "You are invisible to clients and won't receive consultation requests."}
              </p>
            </div>

            <Button
              type="primary"
              icon={<PoweroffOutlined />}
              className="w-full bg-[#A40165] border-[#A40165] hover:bg-[#8A0154] hover:border-[#8A0154]"
              onClick={() => {
                localStorage.clear();
                socket?.disconnect();
                navigate("/signin");
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}

function AdminHeader({ placement, name, subName, onPress }) {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => window.scrollTo(0, 0));

  const showDrawer = () => setVisible(true);
  const hideDrawer = () => setVisible(false);

  return (
    <>
      <div className="flex items-center h-16 m-0 px-6 justify-between w-full ">
        <div className="flex-1 w-full flex items-center ">
          <span className="text-pink-700 hidden sm:block font-extrabold text-4xl  capitalize">
            {subName}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="link"
            onClick={showDrawer}
            className="flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img src={SettingIcon} alt="Settings" className="w-6 h-6" />
          </Button>
        </div>
      </div>

      <Drawer
        className="settings-drawer"
        mask={true}
        width={360}
        onClose={hideDrawer}
        placement={placement}
        visible={visible}
        closeIcon={<CloseOutlined className="pl-2" />}
        title="Settings"
      >
        <div className="flex flex-col">
          <div className="py-6">
            <Button
              type="primary"
              icon={<PoweroffOutlined />}
              className="w-full bg-[#A40165] border-[#A40165] hover:bg-[#8A0154] hover:border-[#8A0154]"
              onClick={() => {
                localStorage.removeItem("authorization");
                localStorage.removeItem("userAuth");
                localStorage.removeItem("Consultantprofile");
                navigate("/signin");
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export { ConsultantHeader as Header, AdminHeader };
